<template>
  <div
    class="h-full z-40 sm:w-[27rem] w-full pb-safe pt-safe sm:absolute fixed right-0 top-0 bg-card border-l border-border flex flex-col"
  >
    <div class="px-2 pt-2 flex">
      <transition name="fade">
        <l-button
          v-if="step !== 'time'"
          icon="bracket-left"
          size="small"
          type="text"
          @click="onBack"
        />
      </transition>
      <l-button icon="close" size="small" type="text" class="ml-auto" @click="$emit('close')" />
    </div>
    <div class="flex-1 pt-2 px-6 pb-4 relative flex flex-col gap-6 min-w-0 overflow-auto">
      <div class="flex-1 relative">
        <transition :name="transitionName">
          <div
            v-if="step === 'time'"
            key="time"
            class="absolute inset-0 flex flex-col gap-6 overflow-y-auto"
          >
            <div class="flex flex-col gap-1">
              <div class="heading-3">
                {{ reservation?.id ? t('reservations.edit') : t('reservations.new') }}
              </div>
              <div class="body-2 text-n-300">
                {{ t('reservations.time-description') }}
              </div>
            </div>
            <div class="flex flex-col gap-4">
              <div class="body-1">{{ t('reservations.details') }}</div>

              <div class="flex items-center gap-4">
                <l-form-field name="diners" class="w-full">
                  <l-form-label :label="t('reservations.diners')" />
                  <l-select v-model="bookingForm.diners" :options="availableDinners">
                    <template #extraContent="{ closeSelector }">
                      <l-button
                        size="small"
                        type="text"
                        color="white"
                        @click="((showCustomDiners = true), closeSelector())"
                      >
                        {{ t('reservations.diners-more') }}
                      </l-button>
                    </template>
                  </l-select>
                </l-form-field>

                <l-form-field name="date" class="w-full">
                  <l-form-label :label="t('reservations.date')" />
                  <l-date-picker
                    v-model:date="day"
                    :show-time="false"
                    :show-icon="false"
                    :default-time="day && format(day, 'HH:mm')"
                    :unavailable-dates="unavailableDates"
                  >
                    <template #default="{ date }">
                      {{ date && format(date, 'dd MMMM') }}
                    </template>
                  </l-date-picker>
                </l-form-field>

                <l-form-field name="time" class="w-full">
                  <l-form-label :label="t('reservations.time')" />
                  <l-select
                    :options="availableHours"
                    :model-value="day?.toISOString()"
                    @update:model-value="v => updateHour(v as string)"
                  />
                </l-form-field>

                <custom-diners
                  v-if="showCustomDiners"
                  :default-value="customDinersValue"
                  @close="showCustomDiners = false"
                  @save="handleCustomDinersSave"
                />
              </div>
            </div>

            <div class="h-[1px] bg-n-600 w-full"></div>

            <div class="body-1">{{ t('reservations.available-hours') }}</div>

            <div
              class="grid grid-cols-3 gap-2 relative"
              v-if="focusedHours.length > 0 || loadingHours"
            >
              <div
                v-if="loadingHours"
                class="flex flex-col items-center justify-center gap-2 py-4 absolute inset-0"
              >
                <l-icon name="status" size="small" class="animate-spin" />
                <div class="body-2 text-n-300">
                  {{ t('reservations.searching-hours') }}
                </div>
              </div>
              <radio-button
                v-for="option in focusedHours"
                :key="option.time"
                v-model="bookingForm.dateTime"
                name="hours"
                :value="option.time"
                :disabled="!option.isAvailable"
                class="transition-opacity duration-300 ease-in-out relative"
                :class="{
                  'opacity-50': !option.isAvailable,
                  'opacity-10 pointer-events-none': loadingHours
                }"
              >
                {{ format(option.time, 'HH:mm') }}
                <div
                  v-if="
                    option.warnings?.includes('ANTICIPATION') || option.warnings?.includes('PACING')
                  "
                  class="absolute right-3 inset-y-0 flex items-center"
                >
                  <l-tooltip :touch="['hold', 300]" placement="bottom">
                    <template #content>
                      <span v-if="option.warnings.includes('ANTICIPATION')">
                        {{ t('reservations.warning.last-minute') }}
                      </span>
                      <span v-if="option.warnings.includes('PACING')">
                        {{ t('reservations.warning.pacing') }}
                      </span>
                    </template>
                    <l-icon name="alert-circle" size="medium" class="text-y-500" />
                  </l-tooltip>
                </div>
              </radio-button>
            </div>

            <div v-else class="text-n-300 body-2">
              {{ t('reservations.no-available-hours') }}
            </div>
            <div
              v-if="focusedHours[0]?.warnings?.includes('DINERS')"
              class="px-4 py-3 bg-y-500/10 border border-y-500/20 rounded text-y-300 flex gap-2"
            >
              <l-icon name="alert-circle" size="medium" />
              <div class="body-3">
                {{ t('reservations.warning.reservations-diners-limit') }}
              </div>
            </div>
          </div>

          <div
            v-else-if="step === 'zone'"
            key="zone"
            class="absolute inset-0 flex flex-col gap-6 overflow-y-auto"
          >
            <div class="flex flex-col gap-1">
              <div class="heading-3">
                {{ reservation?.id ? t('reservations.edit') : t('reservations.new') }}
              </div>
              <div class="body-2 text-n-300">
                {{ t('reservations.zone-description') }}
              </div>
            </div>

            <div class="flex">
              <div class="flex justify-start items-start gap-2 w-full">
                <l-icon name="location" size="medium" />
                <div class="flex flex-col justify-start items-start">
                  <div class="body-2">
                    {{ t('reservations.n-diners', bookingForm.diners!) }}
                  </div>
                  <div class="body-3 text-n-300">{{ location?.name }}</div>
                </div>
              </div>
              <div class="flex justify-start items-start gap-2 w-full">
                <l-icon name="calendar" size="medium" />
                <div class="flex flex-col justify-start items-start">
                  <div class="body-2 !leading-tight">
                    {{
                      intlFormat(
                        day!,
                        {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        },
                        {
                          locale
                        }
                      )
                    }}
                  </div>
                  <div class="body-3 text-n-300">{{ format(bookingForm.dateTime!, 'HH:mm') }}</div>
                </div>
              </div>
            </div>

            <div class="h-[1px] bg-n-600 w-full"></div>

            <div class="flex flex-col gap-4">
              <div class="body-1">{{ t('reservations.settings-preference') }}</div>

              <div class="flex flex-col gap-2 relative">
                <div
                  v-for="option in availableZones"
                  :key="option.id"
                  v-show="!bookingForm.zone || bookingForm.zone === option.id"
                  class="flex flex-col w-full rounded-lg border p-4 transition-all duration-300 ease-in-out"
                  :class="[
                    bookingForm.zone === option.id
                      ? 'border-v-300 relative z-10'
                      : 'border-n-300 cursor-pointer',
                    !bookingForm.zone || bookingForm.zone === option.id
                      ? 'opacity-100'
                      : 'opacity-0',
                    {
                      'border-n-700 text-n-400 cursor-none pointer-events-none': !option.isAvailable
                    }
                  ]"
                  @click="selectZone(option.id)"
                >
                  <div class="flex justify-start items-start gap-2">
                    <l-icon name="people" size="medium" />
                    <div class="flex flex-col justify-start items-start">
                      <div class="body-2">{{ option.title }}</div>
                      <div class="body-3 text-n-300">{{ option.description }}</div>
                    </div>
                  </div>
                  <div
                    class="flex bg-n-700 transition-all duration-300 ease-in-out overflow-hidden"
                    :class="[
                      bookingForm.zone === option.id
                        ? ' h-[4.5rem] opacity-100 mt-2'
                        : 'h-0 opacity-0'
                    ]"
                  >
                    <div class="w-full p-4 flex items-center gap-3">
                      <div class="w-6 h-6 bg-v-300 rounded-full flex items-center justify-center">
                        <l-icon name="check" size="small" />
                      </div>
                      <div class="flex flex-col">
                        <div class="body-3">{{ selectedTables?.join(', ') }}</div>
                        <div class="body-4 text-n-300">{{ selectedFloorplan }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="bookingForm.zone != null" class="flex justify-end">
                <l-button
                  color="white"
                  type="secondary"
                  icon="undo"
                  size="small"
                  @click="showTableSelector = true"
                >
                  {{ t('reservations.change-table') }}
                </l-button>
              </div>
              <div
                v-if="policyWarnings"
                class="px-4 py-3 bg-y-500/10 border border-y-500/20 rounded text-y-300 flex gap-2"
              >
                <l-icon name="alert-circle" size="medium" />
                <div v-if="policyWarnings.length === 1" class="body-3">
                  <span v-if="policyWarnings.includes('ANTICIPATION')">
                    {{ t('reservations.warning.reservation-last-minute') }}
                  </span>
                  <span v-if="policyWarnings.includes('DINERS')">
                    {{ t('reservations.warning.reservations-diners-limit') }}
                  </span>
                  <span v-if="policyWarnings.includes('CAPACITY')">
                    {{ t('reservations.warning.reservations-capacity-limit') }}
                  </span>
                  <span v-if="policyWarnings.includes('PACING')">
                    {{ t('reservations.warning.pacing-limit') }}
                  </span>
                </div>
                <div v-if="policyWarnings.length > 1">
                  <div class="body-2 mb-2">{{ t('reservations.warning.multiple') }}</div>
                  <ul class="list-disc pl-5 body-3">
                    <li v-for="value in policyWarnings" :key="value">
                      <template v-if="value === 'ANTICIPATION'">
                        {{ t('reservations.warning.reservation-last-minute') }}
                      </template>
                      <template v-if="value === 'DINERS'">
                        {{ t('reservations.warning.reservations-diners-limit') }}
                      </template>
                      <template v-if="value === 'CAPACITY'">
                        {{ t('reservations.warning.reservations-capacity-limit') }}
                      </template>
                      <template v-if="value === 'PACING'">
                        {{ t('reservations.warning.pacing-limit') }}
                      </template>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else-if="step === 'client'"
            key="client"
            class="absolute inset-0 flex flex-col gap-4 overflow-y-auto"
          >
            <div class="flex flex-col gap-1">
              <div class="heading-3">
                {{ reservation?.id ? t('reservations.edit') : t('reservations.new') }}
              </div>
              <div class="body-2 text-n-300">
                {{ t('reservations.client-description') }}
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex justify-start items-start gap-2 w-full">
                <l-icon name="people" size="medium" />
                <div class="flex flex-col justify-start items-start">
                  <div class="body-2">{{ t('reservations.n-diners', bookingForm.diners!) }}</div>
                  <div class="body-3 text-n-300">{{ location?.name }}</div>
                </div>
              </div>
              <div class="flex justify-start items-start gap-2 w-full">
                <l-icon name="calendar" size="medium" />
                <div class="flex flex-col justify-start items-start">
                  <div class="body-2 !leading-tight">
                    {{
                      intlFormat(
                        bookingForm.dateTime!,
                        {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        },
                        {
                          locale: locale
                        }
                      )
                    }}
                  </div>
                  <div class="body-3 text-n-300">{{ format(bookingForm.dateTime!, 'HH:mm') }}</div>
                </div>
              </div>
              <div class="flex justify-start items-start gap-2 w-full">
                <l-icon name="location" size="medium" />
                <div class="flex flex-col justify-start items-start">
                  <div class="body-2">{{ t(`reservations.zones.${bookingForm.zone}`) }}</div>
                  <div class="body-3 text-n-300">{{ selectedTables?.join(', ') }}</div>
                </div>
              </div>
            </div>

            <div class="h-[1px] bg-n-600 w-full"></div>

            <client-info ref="formRef" :data="clientForm" @submit="save" />
          </div>
        </transition>
      </div>

      <div class="shrink-0">
        <div v-if="errorMessage" class="text-r-500 text-sm mb-4">
          {{ errorMessage }}
        </div>
        <div class="flex gap-2">
          <l-button class="w-full" size="small" type="secondary" @click="onBack">
            {{ step === 'time' ? t('ctas.cancel') : t('ctas.back') }}
          </l-button>
          <l-button
            class="w-full"
            size="small"
            type="primary"
            :loading="saving"
            :disabled="step === 'time' && loadingHours"
            @click="onContinue"
          >
            {{
              step === 'client'
                ? props.reservation?.id
                  ? t('reservations.save-edit')
                  : t('reservations.save')
                : t('ctas.continue')
            }}
          </l-button>
        </div>
      </div>
    </div>
    <table-selector
      v-model:is-active="showTableSelector"
      mode="seats"
      :title="t('reservations.title')"
      :button-text="t('reservations.accept')"
      :blocked-tables="blockedTables"
      :multiple-select="true"
      :selected-tables="bookingForm.tables"
      :show-actual-status="false"
      @close="showTableSelector = false"
      @tables-selected="selectTables"
    />
  </div>
</template>

<script setup lang="ts">
import {
  addHours,
  eachDayOfInterval,
  eachMinuteOfInterval,
  endOfDay,
  endOfMonth,
  format,
  intlFormat,
  isSameDay,
  isSameMinute,
  startOfDay,
  startOfMonth,
  subHours
} from 'date-fns'
import { storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  LButton,
  LDatePicker,
  LFormField,
  LFormLabel,
  LIcon,
  LSelect,
  LTooltip
} from '@last/core-ui/paprika'

import { reservationsApi } from '@/api/client'
import TableSelector from '@/modules/floorplan/TableSelector.vue'
import { ResponseError } from '@/openapi'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useFloorplanStore } from '@/store/floorplan'
import { useReservationsStore } from '@/store/reservations'
import type { Reservation } from '@/types'

import RadioButton from '../RadioButton.vue'
import ClientInfo from './ClientInfo.vue'
import CustomDiners from './CustomDiners.vue'

type Slot = { start: string; end: string; available: boolean; reasons: string[] }

const props = defineProps<{
  reservation?: Reservation
}>()

const emit = defineEmits<{
  close: []
}>()

const { t, locale } = useI18n()

const { getBlockedTables } = useReservationsStore()
const configStore = useConfigStore()
const floorplanStore = useFloorplanStore()
const authStore = useAuthStore()

const { config } = storeToRefs(configStore)
const { tables } = storeToRefs(floorplanStore)
const { location } = storeToRefs(authStore)
const { hasReservationSubscription } = storeToRefs(authStore)

const toast = useToast()
const formRef = useTemplateRef('formRef')

const availableHoursByZone = ref<Record<string, Slot[]>>({})
const step = ref<'time' | 'zone' | 'client'>('time')
const bookingForm = ref<{
  diners?: number
  dateTime?: string
  zone?: string
  tables?: string[]
}>({})
const clientForm = ref<{
  name: string
  surname: string | null
  phoneNumber: string
  customerComments: string | null
  email: string | null
}>()

const availableDates = ref<Date[]>([])
const visibleMonth = ref<Date>(new Date())

const day = ref<Date>()

const isMovingForward = ref(true)
const showCustomDiners = ref(false)
const showTableSelector = ref(false)
const errorMessage = ref<string | null>(null)

const loadingHours = ref(false)
const saving = ref(false)

onMounted(() => {
  if (props.reservation?.id) {
    bookingForm.value = {
      diners: props.reservation.diners,
      dateTime: props.reservation.dateTime,
      zone: props.reservation.zone,
      tables: props.reservation.tables
    }
    clientForm.value = {
      name: props.reservation.name,
      surname: props.reservation.surname,
      phoneNumber: props.reservation.phoneNumber,
      customerComments: props.reservation.customerComments,
      email: props.reservation.email
    }
    day.value = new Date(props.reservation.dateTime)
  } else {
    initDefaultValues()
  }
})

const transitionName = computed(() => {
  return isMovingForward.value ? 'slide-left' : 'slide-right'
})

const blockedTables = computed(() => {
  return getBlockedTables({
    reservationId: props.reservation?.id,
    customDate: new Date(bookingForm.value.dateTime!)
  })
})

const availableDinners = computed(() => {
  const dinnersLimit = config.value.reservations.dinersLimit ?? 12
  const availableDiners = Array.from({ length: dinnersLimit }, (_, i) => {
    return {
      label: `${i + 1}`,
      value: i + 1
    }
  })

  const { diners } = bookingForm.value
  if (diners && diners > dinnersLimit) {
    availableDiners.push({
      label: `${diners}`,
      value: diners
    })
  }

  return availableDiners
})

const customDinersValue = computed(() => {
  const dinersLimit = config.value.reservations.dinersLimit ?? 12

  return bookingForm.value.diners && bookingForm.value.diners > dinersLimit
    ? bookingForm.value.diners
    : dinersLimit
})

const selectedTables = computed(() => {
  if (!bookingForm.value.tables) return null
  return bookingForm.value.tables.map(t => {
    return tables.value[t].name
  })
})

const selectedFloorplan = computed(() => {
  if (!bookingForm.value.tables) return null
  return floorplanStore.getTableFloorplan(bookingForm.value.tables[0])?.name || '-'
})

const availableHours = computed(() => {
  return eachMinuteOfInterval(
    {
      start: startOfDay(day.value || new Date()),
      end: endOfDay(day.value || new Date())
    },
    {
      step: 15
    }
  ).map(slot => ({
    label: format(slot, 'HH:mm'),
    value: slot.toISOString()
  }))
})

const focusedHours = computed(() => {
  if (!availableHoursByZone.value || !day.value) return []

  const zones = Object.values(availableHoursByZone.value)
  if (zones.length === 0) return []

  const referenceZone = zones[0]
  if (!referenceZone || referenceZone.length === 0) return []

  const hoursWithAvailability = referenceZone.map((slot, index) => {
    const time = new Date(slot.start).toISOString()

    let isAvailable = false
    let warnings: string[] | null = null

    for (const zone of zones) {
      const slotStatus = checkSlotAvailability(zone[index])
      isAvailable = slotStatus.isAvailable
      warnings = slotStatus.warnings
      if (isAvailable) break
    }

    return {
      time,
      isAvailable,
      warnings
    }
  })

  let centerIndex = hoursWithAvailability.findIndex(hour => isSameMinute(hour.time, day.value!))
  centerIndex = centerIndex !== -1 ? centerIndex : 0

  const startIndex = Math.max(0, centerIndex - 4)
  const endIndex = Math.min(hoursWithAvailability.length, startIndex + 9)

  const adjustedStartIndex = Math.max(0, endIndex - 9)

  return hoursWithAvailability.slice(adjustedStartIndex, endIndex)
})

const availableZones = computed(() => {
  if (!availableHoursByZone.value || !bookingForm.value.dateTime) return []

  const zones = Object.keys(availableHoursByZone.value)

  return zones.map(zone => {
    const timeSlots = availableHoursByZone.value[zone] || []
    const isAvailable = timeSlots.some(
      slot =>
        isSameMinute(slot.start, bookingForm.value.dateTime!) &&
        checkSlotAvailability(slot).isAvailable
    )

    const zoneTables = Object.values(tables.value).filter(table => table.zone === zone)
    const zoneCapacity = zoneTables.reduce((acc, table) => acc + table.max, 0)

    return {
      id: zone,
      title: t(`reservations.zones.${zone}`),
      description: t('reservations.zone-capacity', {
        tables: zoneTables.length,
        guests: zoneCapacity
      }),
      isAvailable
    }
  })
})

const policyWarnings = computed(() => {
  if (!bookingForm.value.dateTime) return null
  if (!bookingForm.value.zone) return null

  const slot = availableHoursByZone.value[bookingForm.value.zone]?.find(slot =>
    isSameMinute(slot.start, bookingForm.value.dateTime!)
  )

  return slot?.reasons && slot.reasons.length > 0 ? slot.reasons : null
})

const unavailableDates = computed(() => {
  return eachDayOfInterval({
    start: startOfMonth(visibleMonth.value),
    end: endOfMonth(visibleMonth.value)
  }).filter(day => !availableDates.value.some(date => isSameDay(date, day)))
})

watch(visibleMonth, () => {
  refreshAvailableDays()
})

watch([() => bookingForm.value.diners, day], async ([oldDiners], [newDiners]) => {
  if (oldDiners !== newDiners) {
    await refreshAvailableDays()
    if (newDiners) {
      bookingForm.value.zone = undefined
    }
  }
  await refreshRange()
  // Clean selection if not visible on the list of available hours
  if (
    bookingForm.value.dateTime &&
    !focusedHours.value.some(hour => hour.time === bookingForm.value.dateTime)
  ) {
    bookingForm.value.dateTime = undefined
    bookingForm.value.tables = undefined
    bookingForm.value.zone = undefined
  }
})

async function initDefaultValues() {
  bookingForm.value.diners = 2
  await refreshAvailableDays()
  day.value = await getNextAvailableTime()
}

async function getNextAvailableTime() {
  const now = addHours(new Date(), 2)
  const selectedDay = availableDates.value[0] ?? now

  const startTime = isSameDay(selectedDay, now) ? now : startOfDay(selectedDay)

  const availableHours = await getAvailableHours(selectedDay)
  const availableTimes = Object.values(availableHours ?? {})
    .flat()
    .map(time => new Date(time.start))
    .sort((a, b) => a.getTime() - b.getTime())

  const nextAvailableTime = availableTimes.find(time => time.getTime() >= startTime.getTime())!

  return nextAvailableTime || availableTimes[0]
}

async function refreshAvailableDays() {
  if (!bookingForm.value.diners) return

  availableDates.value = (
    await reservationsApi.getMonthAvailability({
      ignoreReservationId: props.reservation?.id,
      month: format(visibleMonth.value, 'yyyy-MM'),
      partySize: bookingForm.value.diners
    })
  ).map(date => new Date(date))
}

async function getAvailableHours(previewDay: Date) {
  if (!bookingForm.value.diners) return
  return await reservationsApi.getDayAvailability({
    ignoreReservationId: props.reservation?.id,
    date: format(previewDay, 'yyyy-MM-dd'),
    partySize: bookingForm.value.diners
  })
}

async function refreshRange() {
  if (!day.value || !bookingForm.value.diners) return
  loadingHours.value = true
  const availableRange = await reservationsApi.getRangeAvailability({
    ignoreReservationId: props.reservation?.id,
    startDate: subHours(day.value, 2).toISOString(),
    endDate: addHours(day.value, 2).toISOString(),
    partySize: bookingForm.value.diners
  })
  loadingHours.value = false
  if (step.value === 'time') {
    availableHoursByZone.value = availableRange
  }
}

function handleCustomDinersSave(value: number) {
  bookingForm.value.diners = value
  showCustomDiners.value = false
}

function updateHour(hour: string) {
  day.value = new Date(hour)
}

function selectTables(selectedTables: string[]) {
  if (!hasReservationSubscription.value) {
    const table = tables.value[selectedTables[0]]
    bookingForm.value.zone = table.zone
    step.value = 'zone'
  }
  bookingForm.value.tables = selectedTables
  showTableSelector.value = false
}

function checkSlotAvailability(slot: Slot) {
  const allowedReasons = ['ANTICIPATION', 'DINERS', 'CAPACITY', 'PACING']
  const hasOnlyAllowedReasons =
    slot.reasons.length > 0 && slot.reasons.every(reason => allowedReasons.includes(reason))

  return {
    isAvailable: slot.available || hasOnlyAllowedReasons,
    warnings: hasOnlyAllowedReasons ? slot.reasons : null
  }
}

function selectZone(zone: string) {
  if (bookingForm.value.zone) return
  bookingForm.value.zone = zone
  bookingForm.value.tables = []
  bookingForm.value.dateTime = bookingForm.value.dateTime!
  reservationsApi
    .autoassignTable({
      dateTime: bookingForm.value.dateTime!,
      zone: bookingForm.value.zone!,
      diners: +bookingForm.value.diners!
    })
    .then(response => {
      bookingForm.value.tables = response
    })
}

function onBack() {
  isMovingForward.value = false
  if (step.value === 'zone') {
    if (bookingForm.value.zone !== undefined && hasReservationSubscription.value) {
      bookingForm.value.zone = undefined
    } else {
      step.value = 'time'
    }
  } else if (step.value === 'client') {
    step.value = 'zone'
  } else {
    emit('close')
  }
}

function onContinue() {
  isMovingForward.value = true
  if (step.value === 'time') {
    if (!bookingForm.value.dateTime || loadingHours.value) return
    if (!hasReservationSubscription.value) showTableSelector.value = true
    else step.value = 'zone'
  } else if (step.value === 'zone') {
    if (!bookingForm.value.zone) return
    step.value = 'client'
  } else {
    formRef.value?.requestSubmit()
  }
}

async function save(values: any) {
  saving.value = true
  try {
    errorMessage.value = null
    const body = {
      id: props.reservation?.id ?? uuid(),
      dateTime: bookingForm.value.dateTime!,
      diners: bookingForm.value.diners!,
      tables: bookingForm.value.tables!,
      zone: bookingForm.value.zone!,
      name: values.name,
      surname: values.surname ?? '',
      phoneNumber: values.phoneNumber,
      customerComments: values.customerComments,
      email: values.email ?? ''
    }
    if (props.reservation?.id) {
      await reservationsApi.updateReservation({
        reservationId: props.reservation.id,
        createReservationRequest: body
      })
      toast({ title: t('reservations.reservation-updated'), type: 'success' })
    } else {
      await reservationsApi.createReservation({
        createReservationRequest: body
      })
      toast({ title: t('reservations.reservation-created'), type: 'success' })
    }

    emit('close')
  } catch (error) {
    if (error instanceof ResponseError) {
      const responseError = await error.response?.json()
      errorMessage.value = responseError?.error
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-left-enter-to,
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
